// web/src/features/team/player/RosterPlayerModalContent.tsx
/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React, { useState } from 'react'
import { LeftOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { type RosterPlayerFormValues } from '@sportninja/common/features/team/player'
import Text from 'src/components/ts-components/Text'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import useWindowSize from 'src/hooks/useWindowSize'
import { useSidebarState } from 'src/hooks/useSidebarState'
import { MENU_HEIGHT } from 'src/components/css'
import { ScrollSpyLayout } from 'src/components/ScrollSpyLayout'
import ConfirmDiscardChangesModal from 'src/features/profile/player/setupModal/components/ConfirmDiscardChangesModal'
import HeaderCancelIcon from 'src/features/profile/player/setupModal/components/HeaderCancelIcon'
import HeaderSaveIcon from 'src/features/profile/player/setupModal/components/HeaderSaveIcon'
import PlayerFormSkeleton from 'src/features/profile/player/setupModal/components/PlayerFormSkeleton'
import PersonalSection from 'src/features/profile/player/setupModal/components/sections/PersonalSection'
import AddressSection from 'src/features/profile/player/setupModal/components/sections/AddressSection'
import EmergencyContactSection from 'src/features/profile/player/setupModal/components/sections/EmergencyContactSection'
import SocialProfilesSection from 'src/features/profile/player/setupModal/components/sections/SocialProfilesSection'
import MembershipSection from 'src/features/profile/player/setupModal/components/sections/MembershipSection'
import SalesforceIdChip from './components/SalesforceIdChip'
import RosterInfoSection from './components/sections/RosterInfoSection'
import { ROSTER_PLAYER_SETUP_ITEMS } from './rosterPlayerSetupItems'
import { useRosterPlayerContext } from './RosterPlayerContext'

type Props = { isVisible: boolean; transitionDuration: number }

const RosterPlayerModalContent: React.FC<Props> = ({
  isVisible,
  transitionDuration,
}) => {
  const { width } = useWindowSize()
  const isMobile = width <= 768
  const [sidebarCollapsed] = useSidebarState()
  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? 64 : 284

  const {
    isLoading,
    isSeeding,
    onCancel,
    sportId,
    hasAdminPermission,
    requireEmail,
    isEdit,
    isAddExisting,
    emailLocked,
    namesLocked,
    salesforceIdLocked,
  } = useRosterPlayerContext()
  const { submitForm, dirty } = useFormikContext<RosterPlayerFormValues>()

  // Unsaved-changes guard: warn before discarding edits on Back/Cancel; close
  // straight away when the form is untouched.
  const [isConfirmDiscardOpen, setIsConfirmDiscardOpen] = useState(false)
  const handleRequestClose = () => {
    if (dirty) {
      setIsConfirmDiscardOpen(true)
    } else {
      onCancel()
    }
  }

  const handleSave = async () => {
    await submitForm()
  }

  return (
    <div
      css={css`
        ${overlayStyle}
        top: ${isMobile ? 48 : MENU_HEIGHT}px;
        left: ${sidebarWidth}px;
        opacity: ${isVisible ? 1 : 0};
        transform: translateY(${isVisible ? '0' : '20px'});
        transition: opacity ${transitionDuration}ms ease-out,
          transform ${transitionDuration}ms ease-out, left 0.2s ease-in-out;
      `}
    >
      <div css={contentWrapperStyle}>
        <div css={headerContainerStyle}>
          <div css={leftSideStyle}>
            <button
              type='button'
              css={backLinkStyle}
              onClick={handleRequestClose}
            >
              <LeftOutlined aria-hidden='true' />
              <Text
                variant='captionXSmall'
                weight='semiBold'
                color={colors.NEUTRAL_50}
                style='margin: 0; letter-spacing: 1.44px;'
              >
                BACK
              </Text>
            </button>
            <Text
              variant='displaySmall'
              weight='semiBold'
              color={colors.WHITE}
              style={`${
                isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif !important;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif !important;'
              }`}
            >
              {capitalizeWords(isEdit ? 'Edit Player' : 'Add Player')}
            </Text>
          </div>
          <div css={rightSideStyle}>
            <HeaderActionButton
              text='Cancel'
              onClick={handleRequestClose}
              variant='secondary'
              textWeight='medium'
              disabled={isLoading}
              icon={<HeaderCancelIcon />}
            />
            {/* Save stays disabled while the edit form is still seeding — the
                form holds blank values until the fetched player lands. */}
            <HeaderActionButton
              text='Save'
              onClick={handleSave}
              variant='primary'
              textWeight='bold'
              disabled={isLoading || isSeeding}
              isLoading={isLoading}
              icon={<HeaderSaveIcon />}
            />
          </div>
        </div>

        {isLoading || isSeeding ? (
          <PlayerFormSkeleton stacked={isMobile} />
        ) : (
          <ScrollSpyLayout
            // Add-existing (copy): only Personal + Roster Information, plus
            // Membership — the BE keeps the player's own address/emergency/
            // social/membership values, but Membership still hosts the roster
            // identifiers (Customer Identifier + Salesforce ID).
            items={
              isAddExisting
                ? ROSTER_PLAYER_SETUP_ITEMS.filter(
                    (i) =>
                      i.key === 'personal' ||
                      i.key === 'rosterInfo' ||
                      i.key === 'membership'
                  )
                : ROSTER_PLAYER_SETUP_ITEMS
            }
            stacked={isMobile}
            gap={24}
          >
            <ScrollSpyLayout.Sidebar
              width={256}
              ariaLabel='Player form categories'
            />
            <ScrollSpyLayout.Content
              ariaLabel='Roster player sections'
              paddingRight={16}
            >
              <PersonalSection
                requiredNameFields
                requireEmail={requireEmail}
                readOnlyExisting={isAddExisting}
                // Lock email + name only once the player has accepted their
                // invite (identity). Add-new and invited-but-unaccepted keep
                // them editable; add-existing is already read-only.
                lockEmail={emailLocked}
                lockNames={namesLocked}
                // Only while the email is actually editable — a locked or
                // read-only email isn't triggering an invite from this form.
                emailHelperText={
                  isAddExisting || emailLocked
                    ? undefined
                    : 'Adding an email address will send this player an invite to join the team.'
                }
                // Canlan-only, saved-value-only; renders null otherwise.
                extraIdChips={<SalesforceIdChip />}
              />
              <RosterInfoSection sportId={sportId} />
              {!isAddExisting ? (
                <>
                  <AddressSection />
                  <EmergencyContactSection />
                  <SocialProfilesSection />
                </>
              ) : null}
              {/* Roster-form-only: Membership also carries the identifiers, so
                  it renders in add-existing too (where the player's own
                  membership values stay read-only on the BE). */}
              <MembershipSection
                showIdentifiers
                showCustomerIdentifier={hasAdminPermission}
                salesforceIdLocked={salesforceIdLocked}
              />
            </ScrollSpyLayout.Content>
          </ScrollSpyLayout>
        )}
      </div>

      <ConfirmDiscardChangesModal
        open={isConfirmDiscardOpen}
        onCancel={() => setIsConfirmDiscardOpen(false)}
        onConfirm={onCancel}
      />
    </div>
  )
}

export default RosterPlayerModalContent

// ---- styled (cloned from EditPlayerModalContent for pixel parity) ----
const overlayStyle = css`
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 15;
  background-color: ${colors.SECONDARY_400};
  display: flex;
  flex-direction: column;
`

const contentWrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px 88px 0px;

  @media (max-width: 768px) {
    max-width: none;
    padding: 0;
  }
`

const headerContainerStyle = css`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding-bottom: 24px;
  border-bottom: 1px solid ${colors.SOFT_STEEL};
  margin-bottom: 24px;
`

const leftSideStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`

const backLinkStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const rightSideStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`
